import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHead, Panel } from "@/components/kit";
import { EditableRows } from "@/components/EditableRows";
import { api, errMsg } from "@/lib/api";

const SEDI = ["AFRODITE BOUTIQUE HOTEL", "TENUTA LEDO", "CATERING PRESSO SEDE ESTERNA", "Tutte"];

function Registry({ name, cols, title, hint, testId }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/registry/${name}`).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (list) => {
    try {
      const keep = new Set(list.filter((r) => r.id).map((r) => r.id));
      for (const r of rows) if (!keep.has(r.id)) await api.delete(`/registry/${name}/${r.id}`);
      for (const { id, created_at, updated_at, created_by, ...b } of list) {
        if (!Object.values(b).some((v) => v)) continue;
        if (id) await api.put(`/registry/${name}/${id}`, b);
        else await api.post(`/registry/${name}`, b);
      }
      toast.success("Salvato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };
  return <Panel title={title} testId={testId}>{hint && <p className="text-sm text-[#47606B] -mt-2">{hint}</p>}{rows ? <EditableRows cols={cols} rows={rows} onSave={save} testId={`${testId}-rows`} /> : <div className="text-sm text-[#7B929C]">Caricamento…</div>}</Panel>;
}

function FormLinks() {
  const base = `${window.location.origin}/richiesta`;
  const links = [["Sito web", "Sito web"], ["Instagram", "Instagram"], ["Facebook", "Facebook"], ["WhatsApp", "WhatsApp"], ["Fiera / evento", "Fiera / evento"]];
  return (
    <Panel title="Modulo richieste online" testId="form-links">
      <p className="text-sm text-[#47606B] -mt-2">Mettete questi link sul sito, nella bio di Instagram, sui post di Facebook o in un QR alle fiere. Ogni richiesta compare subito in «Richieste» con la sua provenienza, pronta per la risposta su WhatsApp.</p>
      <div className="space-y-2">{links.map(([label, f]) => {
        const url = `${base}?fonte=${encodeURIComponent(f)}`;
        return <div key={f} className="flex items-center gap-2"><span className="w-32 text-sm font-medium shrink-0">{label}</span><input readOnly value={url} className="flex-1 min-w-0 text-xs rounded-md border border-[#D4E6EC] px-3 py-2 bg-[#F5FAFB]" /><Button size="icon" variant="outline" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"))} data-testid={`form-link-copy-${f}`}><Copy size={14} /></Button></div>;
      })}</div>
    </Panel>
  );
}

const TABS = [["collab", "Collaboratori"], ["fornitori", "Fornitori e partner"], ["attrezzature", "Attrezzature"], ["recensioni", "Recensioni"], ["modulo", "Modulo richieste"]];

export default function Team() {
  const [tab, setTab] = useState("collab");
  const [opts, setOpts] = useState({ roles: [], supplier_categories: [] });
  useEffect(() => {
    api.get("/team/options").then((r) => setOpts(r.data));
  }, []);
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Persone, partner e materiali" title="Squadra" />
      <div className="flex gap-2 overflow-x-auto no-scrollbar">{TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${tab === k ? "bg-[#0D2B3A] text-white" : "bg-white border border-[#D4E6EC] hover:border-[#0B6E8F]"}`} data-testid={`team-tab-${k}`}>{l}</button>)}</div>
      {tab === "collab" && <Registry name="collaborators" title="Collaboratori" testId="reg-collab" hint="Camerieri, cuochi ed extra: non hanno accesso all'app, ricevono i turni su WhatsApp."
        cols={[{ k: "nome", label: "Nome e cognome", w: "24%" }, { k: "ruolo", label: "Ruolo", type: "select", options: opts.roles }, { k: "tel", label: "Telefono" }, { k: "costo_orario", label: "€/ora", type: "number", w: "90px" }, { k: "note", label: "Note" }]} />}
      {tab === "fornitori" && <Registry name="suppliers" title="Fornitori e partner" testId="reg-suppliers" hint="Fiorista, fotografo, musica, wedding planner… con la provvigione che vi riconoscono."
        cols={[{ k: "nome", label: "Nome", w: "22%" }, { k: "categoria", label: "Categoria", type: "select", options: opts.supplier_categories }, { k: "tel", label: "Telefono" }, { k: "email", label: "Email" }, { k: "commissione", label: "Provvigione %", type: "number", w: "100px" }, { k: "note", label: "Note" }]} />}
      {tab === "attrezzature" && <Registry name="equipment" title="Attrezzature e allestimenti" testId="reg-equipment" hint="Quantità totali disponibili: l'app avvisa se nello stesso giorno ne servono più di quante ne avete."
        cols={[{ k: "nome", label: "Articolo", w: "40%" }, { k: "quantita", label: "Quantità totale", type: "number", w: "140px" }, { k: "note", label: "Note" }]} />}
      {tab === "recensioni" && <Registry name="review_links" title="Dove recensirci" testId="reg-reviews" hint="Dopo il questionario, i clienti soddisfatti (voto medio da 4 in su) vedono questi pulsanti. Aggiungete pure TripAdvisor, Matrimonio.com, Facebook…"
        cols={[{ k: "sede", label: "Struttura", type: "select", options: SEDI, w: "28%" }, { k: "canale", label: "Canale", ph: "Google, TripAdvisor…", w: "20%" }, { k: "url", label: "Link", ph: "https://…" }]} />}
      {tab === "modulo" && <FormLinks />}
    </div>
  );
}
